import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { useTRPC } from "@/lib/trpc";
import type { BrandDashboardPeriod } from "@/state/store/userPreferences";

interface LlmResponseCountersProps {
      brandId: string;
      period: BrandDashboardPeriod;
}

export function LlmResponseCounters({ brandId, period }: LlmResponseCountersProps) {
      const trpc = useTRPC();
      const { data, isLoading } = useQuery(
            trpc.charts.getBrandLlmResponseCountsData.queryOptions(
                  { brandId, ...getDisplayPeriodQuery(period) },
                  { enabled: !!brandId },
            ),
      );

      if (isLoading) {
            return (
                  <div className="flex flex-wrap items-center gap-1.5" aria-label="Loading LLM response counts">
                        {Array.from({ length: 4 }).map((_, i) => (
                              <Skeleton key={i} className="h-9 w-28 rounded-full" />
                        ))}
                  </div>
            );
      }

      if (!data || data.length === 0) {
            return null;
      }

      return (
            <TooltipProvider delayDuration={150}>
                  <section
                        aria-label="LLM response counts"
                        className="flex min-w-0 flex-wrap items-center gap-1.5"
                  >
                        {data.map((item) => (
                              <Tooltip key={item.id}>
                                    <TooltipTrigger asChild>
                                          <button
                                                type="button"
                                                aria-label={`${item.modelLabel || item.label}: ${item.responseCount.toLocaleString()} responses`}
                                                className="group relative flex h-9 max-w-full items-center gap-2 rounded-full border border-transparent bg-sidebar px-1.5 py-1 pr-2.5 transition-all hover:border-sidebar-border hover:bg-sidebar-accent/70"
                                          >
                                                <div className="relative flex size-7 shrink-0 items-center justify-center rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)]">
                                                      <img
                                                            src={item.logo}
                                                            alt=""
                                                            className="size-4.5 rounded-sm object-contain"
                                                            onError={(e) => {
                                                                  const target = e.target as HTMLImageElement;
                                                                  target.style.display = "none";
                                                            }}
                                                      />
                                                </div>
                                                <span className="max-w-28 truncate text-xs font-medium text-sidebar-foreground/70 transition-colors group-hover:text-sidebar-foreground/90 sm:max-w-36">
                                                      {item.modelLabel || item.label}
                                                </span>
                                                <span className="text-xs font-semibold tabular-nums text-sidebar-foreground/60 transition-colors group-hover:text-sidebar-foreground/80">
                                                      {item.responseCount.toLocaleString()}
                                                </span>
                                          </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" sideOffset={8} className="max-w-56">
                                          <div className="space-y-0.5">
                                                <p className="font-medium text-foreground">
                                                      {item.modelLabel || item.label}
                                                </p>
                                                <p className="text-muted-foreground">
                                                      {item.providerLabel} · {item.responseCount.toLocaleString()} response
                                                      {item.responseCount === 1 ? "" : "s"}
                                                </p>
                                          </div>
                                    </TooltipContent>
                              </Tooltip>
                        ))}
                  </section>
            </TooltipProvider>
      );
}
